// src/store/profileStore.js (or a suitable location)
import Vue from 'vue'; // For Vue 2
// For Vue 3 you would use: import { reactive } from 'vue';
import { doc, getDoc, onSnapshot, setDoc, collection } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '@/firebase/init';
// For Vue 2, Vue.observable makes an object reactive
export const profileStore = Vue.observable({
  data: null,
  isLoading: false,
  error: null,
  lastFetchedUid: null,
  unsubscribe: null,

  async fetchProfile(forceRefresh = false) {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      this.clearProfile();
      return null;
    }

    if (!forceRefresh && this.data && this.lastFetchedUid === user.uid) {
      console.log('ProfileStore: Returning cached profile for', user.uid);
      return this.data;
    }

    console.log('ProfileStore: Fetching profile for', user.uid);
    this.isLoading = true;
    this.error = null;
    this.lastFetchedUid = user.uid;

    try {
      const profileDocRef = doc(db, 'users', user.uid);
      
      // Cancel any existing listener
      if (this.unsubscribe) {
        this.unsubscribe();
        this.unsubscribe = null;
      }
      
      // One-time fetch for immediate return
      const profileSnap = await getDoc(profileDocRef);
      
      if (profileSnap.exists()) {
        this.data = { uid: user.uid, ...profileSnap.data() };
        console.log('ProfileStore: Profile fetched:', this.data);
      } else {
        console.log('ProfileStore: No profile document found for', user.uid);
        this.data = null;
        
        // Create user document if it doesn't exist
        await this.ensureUserDocument(user);
      }
      
      // Set up real-time listener for future updates
      this.unsubscribe = onSnapshot(profileDocRef, (doc) => {
        if (doc.exists()) {
          this.data = { uid: user.uid, ...doc.data() };
          console.log('ProfileStore: Profile updated via snapshot:', this.data);
        } else {
          console.log('ProfileStore: Profile document deleted or not found');
          this.data = null;
        }
      }, (error) => {
        console.error('ProfileStore: Error in snapshot listener:', error);
        this.error = error;
      });
      
      return this.data;
    } catch (e) {
      console.error("ProfileStore: Error fetching user profile:", e);
      this.error = e;
      this.data = null; // Clear data on error
      return null;
    } finally {
      this.isLoading = false;
    }
  },

  async ensureUserDocument(user) {
    if (!user) {
      user = getAuth().currentUser;
      if (!user) return null;
    }

    console.log('ProfileStore: Creating user document for', user.uid);
    
    try {
      const userRef = doc(collection(db, 'users'), user.uid);
      
      // Create default user document
      await setDoc(userRef, {
        id: user.uid,
        email: user.email,
        username: '',
        name: user.displayName || '',
        avatar: user.photoURL || '',
        sparks: 0,
        createdAt: new Date()
      }, { merge: true });
      
      // Fetch the newly created document to update store
      const newProfileSnap = await getDoc(userRef);
      if (newProfileSnap.exists()) {
        this.data = { uid: user.uid, ...newProfileSnap.data() };
        console.log('ProfileStore: Created and fetched new profile:', this.data);
      }
      
      return this.data;
    } catch (e) {
      console.error("ProfileStore: Error creating user document:", e);
      this.error = e;
      return null;
    }
  },

  clearProfile() {
    console.log('ProfileStore: Clearing profile data.');
    // Clean up snapshot listener if exists
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    this.data = null;
    this.isLoading = false;
    this.error = null;
    this.lastFetchedUid = null;
  },

  // Helper getter for username
  get username() {
    // IMPORTANT: Replace 'username' with your actual username field
    return this.data && this.data.username ? this.data.username.trim() : '';
  },

  get hasValidUsername() {
    return !!(this.username && this.username !== '');
  }
});